import { Camera, Megaphone, Mic, Square } from 'lucide-react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, hlaskaZChyby } from '../../api/client.js';
import type { DenikZaznamDetail, Vyzva, VyzvaAdresat } from '../../api/types.js';
import { Banner, Chyba, Nacitani, Textarea, VelkeTlacitko } from '../../components/ui.js';
import { useDiktovani } from '../../lib/diktovani.js';
import { formatDen } from '../../lib/format.js';
import { denPlus } from './datum.js';
import { DatumVolba } from './DatumVolba.js';
import { sledujUdalost } from '../../lib/analytika.js';
import { odesliNeboZarad } from '../../lib/offline.js';

/**
 * VÝZVA TDI (wireframe 13 §1, checklist E1 — celkem ≤5 ťuknutí):
 * 1. komu (lidé zakázky — výběr rovnou posouvá dál),
 * 2. text (diktát nebo šablona „přejímka"),
 * 3. termín (rychlé volby Zítra/Pozítří) + potvrzení; vazba na dnešní záznam
 *    se připojí automaticky. Jeden request POST /api/vyzvy. Váže se i na
 *    podepsaný den. Po odeslání jasné potvrzení (A3.3).
 */

const SABLONA_PREJIMKA =
  'Zítra se konstrukce zakrývá — výzva k přejímce, dokud je provedení vidět.';

export function VyzvaWizard({
  zaznam,
  zavri,
}: {
  zaznam: DenikZaznamDetail;
  zavri: (hotovo: boolean) => void;
}) {
  const [krok, setKrok] = useState<1 | 2 | 3>(1);
  const [typ, setTyp] = useState<'vyjadreni' | 'prejimka'>('vyjadreni');
  const [fotky, setFotky] = useState<File[]>([]);
  const [adresat, setAdresat] = useState<VyzvaAdresat | null>(null);
  const [text, setText] = useState('');
  const [termin, setTermin] = useState(denPlus(1));
  const [odesilam, setOdesilam] = useState(false);
  const [chyba, setChyba] = useState<string | null>(null);
  const [odeslano, setOdeslano] = useState(false);

  const diktovani = useDiktovani((usek) => setText((t) => (t ? `${t.trimEnd()} ${usek}` : usek)));

  const { data: lide, isLoading } = useQuery({
    queryKey: ['vyzvy-adresati'],
    queryFn: () => api.get<VyzvaAdresat[]>('/api/vyzvy/adresati'),
    staleTime: 10 * 60 * 1000,
  });

  const odeslat = async () => {
    if (!adresat || !text.trim()) return;
    setOdesilam(true);
    setChyba(null);
    try {
      // Foto „před zakrytím" patří k deníkovému dni (výzva na den odkazuje) —
      // důkaz stavu konstrukce, i kdyby TDI nepřišel.
      for (const f of fotky) {
        await odesliNeboZarad({
          druh: 'soubor',
          url: `/api/diary/${zaznam.id}/prilohy`,
          soubor: {
            blob: f,
            nazevSouboru: f.name || 'foto.jpg',
            mimeType: f.type || 'image/jpeg',
            popis: 'Foto před zakrytím (přejímka)',
          },
          popis: 'Foto před zakrytím',
        });
      }
      await api.post<Vyzva>('/api/vyzvy', {
        adresatUserId: adresat.id,
        predmetTyp: typ === 'prejimka' ? 'prejimka' : 'denik_zaznam',
        predmetId: zaznam.id,
        text: text.trim(),
        termin: termin ? new Date(`${termin}T08:00:00.000Z`).toISOString() : null,
      });
      sledujUdalost('vyzva_zalozena');
      setOdeslano(true);
    } catch (err) {
      setChyba(hlaskaZChyby(err));
      setOdesilam(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-overlay p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={odeslano ? 'Výzva odeslána' : `Výzva TDI — krok ${krok} ze 3`}
        className="mx-auto flex w-full max-w-md flex-col gap-4 rounded-2xl bg-surface p-5 shadow-xl"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">
            {odeslano ? (
              '✓ Výzva odeslána'
            ) : (
              <span className="flex items-center gap-2">
                <Megaphone size={20} strokeWidth={2} aria-hidden="true" /> VÝZVA TDI {krok}/3
              </span>
            )}
          </h2>
          <button
            type="button"
            className="min-h-12 min-w-12 text-2xl"
            aria-label="Zavřít"
            onClick={() => zavri(odeslano)}
          >
            ×
          </button>
        </div>

        {chyba ? <Chyba>{chyba}</Chyba> : null}

        {odeslano ? (
          <>
            <Banner ton="success">
              Výzva odeslána — stav uvidíte u dnešního záznamu v deníku.
            </Banner>
            <VelkeTlacitko onClick={() => zavri(true)}>Hotovo</VelkeTlacitko>
          </>
        ) : null}

        {!odeslano && krok === 1 && (
          <>
            {/* Typ výzvy — dvě velké volby s radio kolečkem (vzor formulářů). */}
            <div role="radiogroup" aria-label="Typ výzvy" className="flex flex-col gap-2">
              {[
                {
                  id: 'vyjadreni' as const,
                  nazev: 'Vyjádření k zápisu',
                  popis: 'TDI se vyjádří k dnešnímu záznamu v deníku.',
                },
                {
                  id: 'prejimka' as const,
                  nazev: 'PŘEJÍMKA zakrývané konstrukce',
                  popis: 'Zítra se zakrývá — bez přejímky hrozí neproplacení. Povinné foto.',
                },
              ].map((v) => (
                <button
                  key={v.id}
                  type="button"
                  role="radio"
                  aria-checked={typ === v.id}
                  onClick={() => setTyp(v.id)}
                  className={`flex min-h-14 items-center gap-3 rounded-xl border-2 px-3 py-2 text-left ${
                    typ === v.id
                      ? 'border-primary bg-primary-tint'
                      : 'border-border-strong bg-surface'
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`flex size-5 shrink-0 items-center justify-center rounded-full border-2 ${
                      typ === v.id ? 'border-primary' : 'border-border-strong'
                    }`}
                  >
                    {typ === v.id && <span className="size-2.5 rounded-full bg-primary" />}
                  </span>
                  <span className="min-w-0">
                    <span
                      className={`block text-base ${typ === v.id ? 'font-bold text-primary' : 'font-semibold'}`}
                    >
                      {v.nazev}
                    </span>
                    <span className="block text-sm font-normal text-text-secondary">{v.popis}</span>
                  </span>
                </button>
              ))}
            </div>
            <p className="text-lg font-semibold">Komu?</p>
            {isLoading && <Nacitani text="Načítám lidi zakázky…" />}
            {lide && lide.length === 0 && (
              <p className="text-base text-text-secondary">Žádní lidé — požádejte kancelář.</p>
            )}
            <div className="flex flex-col gap-2">
              {(lide ?? []).map((l) => (
                <button
                  key={l.id}
                  type="button"
                  className="min-h-14 rounded-xl border-2 border-border-strong bg-surface px-4 text-left hover:bg-bg"
                  onClick={() => {
                    setAdresat(l);
                    setKrok(2);
                  }}
                >
                  <span className="block text-lg font-semibold">{l.jmeno}</span>
                  {l.role.length > 0 && (
                    <span className="block text-sm text-text-secondary">{l.role.join(', ')}</span>
                  )}
                </button>
              ))}
            </div>
            {/* Hint proč se vybírá „kdo od nás" — drobně pod polem, ne dominantní
                (blueprint 16 §14). */}
            <p className="text-sm text-text-muted">
              TDI zatím do aplikace nemá přístup — vyberte, kdo od nás výzvu předá a zaeviduje
              vyjádření.
            </p>
          </>
        )}

        {!odeslano && krok === 2 && (
          <>
            <p className="text-base text-text-secondary">
              Komu: <span className="font-semibold">{adresat?.jmeno}</span>
            </p>
            {diktovani.podporovano && (
              <VelkeTlacitko
                varianta={diktovani.diktuji ? 'podpis' : 'primary'}
                onClick={diktovani.prepni}
              >
                {diktovani.diktuji ? (
                  <>
                    <Square size={16} fill="currentColor" aria-hidden="true" /> ZASTAVIT DIKTÁT
                  </>
                ) : (
                  <>
                    <Mic size={18} strokeWidth={2} aria-hidden="true" /> NADIKTOVAT
                  </>
                )}
              </VelkeTlacitko>
            )}
            <VelkeTlacitko varianta="secondary" onClick={() => setText(SABLONA_PREJIMKA)}>
              šablona: přejímka
            </VelkeTlacitko>
            <Textarea
              aria-label="Text výzvy"
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="K čemu vyzýváte… (např. přejímka výztuže stropu před betonáží)"
            />
            {diktovani.prubezny ? (
              <p aria-live="polite" className="text-base text-text-secondary italic">
                {diktovani.prubezny}…
              </p>
            ) : null}
            {typ === 'prejimka' && (
              <div className="flex flex-col gap-2 rounded-xl border-2 border-dashed border-warning-border bg-warning-bg p-3">
                <p className="text-base font-semibold text-warning-text">
                  Foto před zakrytím (povinné) — {fotky.length} ks
                </p>
                {fotky.length > 0 && (
                  <div className="flex gap-2 overflow-x-auto">
                    {fotky.map((f, i) => (
                      <img
                        key={i}
                        src={URL.createObjectURL(f)}
                        alt={`Foto ${i + 1}`}
                        className="h-16 w-16 shrink-0 rounded-lg object-cover"
                      />
                    ))}
                  </div>
                )}
                <label className="flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-lg bg-primary px-3 font-bold text-white">
                  <Camera size={18} strokeWidth={2} aria-hidden="true" /> VYFOTIT
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    multiple
                    className="hidden"
                    aria-label="Vyfotit před zakrytím"
                    onChange={(e) => {
                      if (e.target.files) setFotky((f) => [...f, ...Array.from(e.target.files!)]);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            )}
            <VelkeTlacitko
              onClick={() => setKrok(3)}
              disabled={!text.trim() || (typ === 'prejimka' && fotky.length === 0)}
            >
              Dál
            </VelkeTlacitko>
          </>
        )}

        {!odeslano && krok === 3 && (
          <>
            <DatumVolba popisek="Termín vyjádření" hodnota={termin} onZmena={setTermin} />
            <p className="text-base text-text-secondary">
              Vazba: záznam {formatDen(zaznam.datum)} — připojí se automaticky.
            </p>
            {typ === 'prejimka' && (
              <p className="text-sm font-semibold text-warning-text">
                Přejímka musí mít termín — kdy se konstrukce zakrývá.
              </p>
            )}
            <VelkeTlacitko
              onClick={() => void odeslat()}
              disabled={odesilam || (typ === 'prejimka' && !termin)}
            >
              {odesilam ? 'Odesílám…' : 'ODESLAT VÝZVU'}
            </VelkeTlacitko>
          </>
        )}
      </div>
    </div>
  );
}
